// Command Palette — Ctrl/Cmd+K ile açılan global komut/arama paleti.
// Kullanım:
//   <CommandPalette open={open} onClose={...} onNavigate={...}
//                   menuItems={[{ key, label, perm? }]}
//                   quickActions={[{ label, hint?, onAction }]} />
//
// Içinde: search input + grouped results + ↑↓Enter klavye navigasyonu + dinamik müşteri/stok araması.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import api from '../services/api';

export default function CommandPalette({ open, onClose, onNavigate, menuItems = [], quickActions = [] }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loadingDyn, setLoadingDyn] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Açıldığında reset + odakla
  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      setAccounts([]);
      setStocks([]);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Dinamik müşteri/stok arama (debounced)
  useEffect(() => {
    if (!open || !q || q.length < 2) {
      setAccounts([]);
      setStocks([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoadingDyn(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const [aR, sR] = await Promise.all([
          api.get('/accounts', { params: { search: q, limit: 5 } }).catch(() => null),
          api.get('/stocks',   { params: { search: q, limit: 5 } }).catch(() => null),
        ]);
        setAccounts(aR?.data?.data || []);
        setStocks(sR?.data?.data   || []);
      } finally { setLoadingDyn(false); }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [q, open]);

  // Filtrelenmiş sonuçlar (groups halinde)
  const groups = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const pageMatches = menuItems
      .filter(it => !qq || (it.label || '').toLowerCase().includes(qq) || (it.key || '').includes(qq))
      .slice(0, 8);

    const actionMatches = quickActions
      .filter(a => !qq || a.label.toLowerCase().includes(qq))
      .slice(0, 6);

    const out = [];
    if (actionMatches.length) {
      out.push({ key: 'actions', label: 'Hızlı Eylemler',
        items: actionMatches.map(a => ({ ...a, kind: 'action' })) });
    }
    if (pageMatches.length) {
      out.push({ key: 'pages', label: 'Sayfalar',
        items: pageMatches.map(it => ({ kind: 'page', key: it.key, label: it.label, hint: it.parentLabel })) });
    }
    if (accounts.length) {
      out.push({ key: 'accounts', label: 'Müşteriler',
        items: accounts.map(a => ({ kind: 'account', key: a.id, label: a.title || a.code,
          hint: [a.code, a.phone, a.city].filter(Boolean).join(' · ') })) });
    }
    if (stocks.length) {
      out.push({ key: 'stocks', label: 'Stoklar',
        items: stocks.map(s => ({ kind: 'stock', key: s.id, label: s.name || s.code,
          hint: [s.code, s.barcode, s.category_name].filter(Boolean).join(' · ') })) });
    }
    return out;
  }, [q, menuItems, quickActions, accounts, stocks]);

  // Flat list (klavye için)
  const flat = useMemo(() => groups.flatMap(g => g.items.map(it => ({ ...it, group: g.key }))), [groups]);

  // active'i flat'a senkronla
  useEffect(() => {
    if (active >= flat.length) setActive(0);
  }, [flat.length, active]);

  const executeItem = useCallback((it) => {
    if (!it) return;
    if (it.kind === 'page') {
      onNavigate && onNavigate(it.key);
    } else if (it.kind === 'action') {
      it.onAction && it.onAction();
    } else if (it.kind === 'account') {
      onNavigate && onNavigate('accounts');
    } else if (it.kind === 'stock') {
      onNavigate && onNavigate('stocks');
    }
    onClose();
  }, [onNavigate, onClose]);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, flat.length - 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0));                return; }
    if (e.key === 'Enter')     { e.preventDefault(); executeItem(flat[active]);                          return; }
  }, [flat, active, onClose, executeItem]);

  if (!open) return null;

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.searchIcon}>⌕</span>
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Sayfa, müşteri, stok, eylem ara..."
            style={S.input}
            autoComplete="off"
          />
          {loadingDyn && <span style={S.spinnerSmall}>...</span>}
          <kbd style={S.kbd}>Esc</kbd>
        </div>

        <div style={S.results}>
          {groups.length === 0 && (
            <div style={S.empty}>
              {q ? 'Sonuç bulunamadı' : 'Aramaya başla... Sayfa adı, müşteri, stok veya eylem yaz.'}
            </div>
          )}
          {(() => {
            let idx = -1;
            return groups.map(group => (
              <div key={group.key} style={{ marginBottom: 6 }}>
                <div style={S.groupHeader}>{group.label}</div>
                {group.items.map(it => {
                  idx++;
                  const isActive = idx === active;
                  return (
                    <button
                      key={group.key + '-' + it.key}
                      type="button"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => executeItem(it)}
                      style={{ ...S.item, ...(isActive ? S.itemActive : null) }}
                    >
                      <span style={S.itemIcon}>{KIND_ICONS[it.kind] || '•'}</span>
                      <span style={S.itemLabel}>{it.label}</span>
                      {it.hint && <span style={S.itemHint}>{it.hint}</span>}
                      {isActive && <kbd style={{ ...S.kbd, marginLeft: 'auto' }}>↵</kbd>}
                    </button>
                  );
                })}
              </div>
            ));
          })()}
        </div>

        <div style={S.footer}>
          <span><kbd style={S.kbd}>↑</kbd><kbd style={S.kbd}>↓</kbd> gez</span>
          <span><kbd style={S.kbd}>↵</kbd> seç</span>
          <span><kbd style={S.kbd}>Esc</kbd> kapat</span>
        </div>
      </div>
    </div>
  );
}

const KIND_ICONS = {
  page: '📄',
  action: '⚡',
  account: '👤',
  stock: '📦',
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9000,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', justifyContent: 'center',
    paddingTop: '10vh', paddingLeft: 16, paddingRight: 16,
    animation: 'cmdk-fade 0.12s ease-out',
  },
  modal: {
    width: '100%', maxWidth: 560, maxHeight: '70vh',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-xl)',
    boxShadow: 'var(--shadow-lg)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    animation: 'cmdk-pop 0.15s ease-out',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-secondary)',
  },
  searchIcon: { fontSize: 18, color: 'var(--text-tertiary)' },
  input: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 'var(--text-lg)',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
  },
  spinnerSmall: { color: 'var(--text-tertiary)', fontSize: 13 },
  kbd: {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.65rem',
    padding: '2px 6px',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-xs)',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-secondary)',
    fontWeight: 600,
  },
  results: {
    flex: 1,
    overflowY: 'auto',
    padding: 6,
  },
  groupHeader: {
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--text-tertiary)',
    padding: '8px 10px 4px',
  },
  item: {
    width: '100%',
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--r-md)',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-sm)',
    textAlign: 'left',
    transition: 'background 0.08s',
  },
  itemActive: {
    background: 'var(--accent-glow)',
    color: 'var(--accent-primary)',
  },
  itemIcon: { width: 22, textAlign: 'center', flexShrink: 0 },
  itemLabel: { fontWeight: 500 },
  itemHint: {
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
    marginLeft: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  empty: {
    padding: 30, textAlign: 'center',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--text-sm)',
    fontStyle: 'italic',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 14,
    padding: '8px 14px',
    borderTop: '1px solid var(--border-secondary)',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-tertiary)',
    background: 'var(--bg-card)',
  },
};

if (typeof document !== 'undefined' && !document.getElementById('cmdk-keyframes')) {
  const s = document.createElement('style');
  s.id = 'cmdk-keyframes';
  s.textContent = `
    @keyframes cmdk-fade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes cmdk-pop  { from { transform: translateY(-6px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
  `;
  document.head.appendChild(s);
}
